import React, { useEffect, useMemo, useState } from "react";
import { X, Hash, Lock, Search, Briefcase, FileText, User as UserIcon, Check } from "lucide-react";
import { toast } from "sonner";
import { chatApi, jobsApi, estimatesApi, customersApi } from "../../lib/api";
import ChatAvatar from "./ChatAvatar";

function Shell({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={onClose} data-testid={testid}>
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" />
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden"
      >
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-[15px] font-bold text-slate-900">{title}</h3>
          <button onClick={onClose} className="h-7 w-7 rounded-md hover:bg-slate-100 flex items-center justify-center">
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

const inputCls =
  "w-full h-9 px-3 rounded-md border border-slate-200 bg-white text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400";

/** Multi-select list of company members. */
function MemberMultiSelect({ members, selected, onToggle, excludeIds = [] }) {
  const [q, setQ] = useState("");
  const list = members.filter(
    (m) => !excludeIds.includes(m.id) && (m.name || "").toLowerCase().includes(q.toLowerCase())
  );
  return (
    <div>
      <div className="relative mb-2">
        <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people…" className={`${inputCls} pl-8`} data-testid="member-search" />
      </div>
      <div className="max-h-56 overflow-y-auto space-y-0.5">
        {list.map((m) => {
          const on = selected.includes(m.id);
          return (
            <button
              key={m.id}
              onClick={() => onToggle(m.id)}
              data-testid={`member-option-${m.id}`}
              className={`w-full flex items-center gap-2.5 px-2 py-1.5 rounded-md text-left transition-colors ${on ? "bg-blue-50" : "hover:bg-slate-50"}`}
            >
              <ChatAvatar name={m.name} avatarUrl={m.avatarUrl} presence={m.presence} size="sm" />
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium text-slate-800 truncate">{m.name}</div>
                <div className="text-[11px] text-slate-400 truncate capitalize">{(m.role || "").replace("_", " ")}</div>
              </div>
              {on && <Check className="h-4 w-4 text-blue-600" />}
            </button>
          );
        })}
        {list.length === 0 && <div className="text-[12px] text-slate-400 py-3 text-center">No people found.</div>}
      </div>
    </div>
  );
}

export function NewChannelModal({ members, currentUserId, onClose, onCreated }) {
  const [type, setType] = useState("channel");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);

  const create = async () => {
    if (!name.trim()) return toast.error("Give it a name");
    setSaving(true);
    try {
      const conv = await chatApi.createChannel({ type, name: name.trim(), description: description.trim(), memberIds: selected });
      toast.success(`${type === "channel" ? "Channel" : "Group"} created`);
      onCreated(conv);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Shell title="Create a channel" onClose={onClose} testid="new-channel-modal">
      <div className="p-5 space-y-3">
        <div className="grid grid-cols-2 gap-2">
          {[
            { k: "channel", label: "Public channel", icon: Hash, hint: "Anyone can join" },
            { k: "group", label: "Private group", icon: Lock, hint: "Invite only" },
          ].map((o) => {
            const Icon = o.icon;
            const on = type === o.k;
            return (
              <button
                key={o.k}
                onClick={() => setType(o.k)}
                data-testid={`channel-type-${o.k}`}
                className={`flex flex-col items-start gap-0.5 p-3 rounded-lg border text-left transition-colors ${on ? "border-blue-500 bg-blue-50/50 ring-1 ring-blue-500/30" : "border-slate-200 hover:border-slate-300"}`}
              >
                <Icon className={`h-4 w-4 ${on ? "text-blue-600" : "text-slate-500"}`} />
                <span className="text-[12.5px] font-semibold text-slate-800">{o.label}</span>
                <span className="text-[11px] text-slate-400">{o.hint}</span>
              </button>
            );
          })}
        </div>
        <div>
          <label className="block text-[11px] font-semibold text-slate-500 mb-1">Name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder={type === "channel" ? "e.g. dispatch" : "e.g. Leadership"} className={inputCls} data-testid="channel-name-input" />
        </div>
        <div>
          <label className="block text-[11px] font-semibold text-slate-500 mb-1">Description <span className="font-normal text-slate-400">(optional)</span></label>
          <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What's this for?" className={inputCls} />
        </div>
        <div>
          <label className="block text-[11px] font-semibold text-slate-500 mb-1">Add people {type === "group" ? "(required for private groups)" : "(optional)"}</label>
          <MemberMultiSelect
            members={members}
            selected={selected}
            excludeIds={[currentUserId]}
            onToggle={(id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]))}
          />
        </div>
      </div>
      <div className="px-5 py-3 border-t border-slate-100 flex justify-end gap-2">
        <button onClick={onClose} className="h-9 px-3.5 rounded-md text-[13px] font-semibold text-slate-600 hover:bg-slate-100">Cancel</button>
        <button onClick={create} disabled={saving} data-testid="create-channel-submit" className="h-9 px-4 rounded-md bg-blue-600 text-white text-[13px] font-semibold hover:bg-blue-700 disabled:opacity-60">
          {saving ? "Creating…" : "Create"}
        </button>
      </div>
    </Shell>
  );
}

export function NewDmModal({ members, currentUserId, onClose, onStarted }) {
  const [q, setQ] = useState("");
  const list = members.filter((m) => m.id !== currentUserId && (m.name || "").toLowerCase().includes(q.toLowerCase()));
  const start = async (userId) => {
    try {
      const conv = await chatApi.startDm(userId);
      onStarted(conv);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start DM");
    }
  };
  return (
    <Shell title="New direct message" onClose={onClose} testid="new-dm-modal">
      <div className="p-5">
        <div className="relative mb-2">
          <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search teammates…" className={`${inputCls} pl-8`} data-testid="dm-search" />
        </div>
        <div className="max-h-72 overflow-y-auto space-y-0.5">
          {list.map((m) => (
            <button
              key={m.id}
              onClick={() => start(m.id)}
              data-testid={`dm-option-${m.id}`}
              className="w-full flex items-center gap-2.5 px-2 py-1.5 rounded-md text-left hover:bg-slate-50"
            >
              <ChatAvatar name={m.name} avatarUrl={m.avatarUrl} presence={m.presence} size="sm" />
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium text-slate-800 truncate">{m.name}</div>
                <div className="text-[11px] text-slate-400 truncate capitalize">{(m.role || "").replace("_", " ")} · {m.presence}</div>
              </div>
            </button>
          ))}
          {list.length === 0 && <div className="text-[12px] text-slate-400 py-4 text-center">No teammates found.</div>}
        </div>
      </div>
    </Shell>
  );
}

export function BrowseChannelsModal({ onClose, onJoined }) {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    chatApi.publicChannels().then(setChannels).catch(() => setChannels([])).finally(() => setLoading(false));
  }, []);
  const join = async (id) => {
    try {
      await chatApi.join(id);
      toast.success("Joined channel");
      onJoined(id);
    } catch {
      toast.error("Could not join");
    }
  };
  return (
    <Shell title="Browse channels" onClose={onClose} testid="browse-channels-modal">
      <div className="p-5 max-h-80 overflow-y-auto space-y-1.5">
        {loading && <div className="text-[12px] text-slate-400 py-4 text-center">Loading…</div>}
        {!loading && channels.length === 0 && <div className="text-[12px] text-slate-400 py-4 text-center">No other public channels to join.</div>}
        {channels.map((c) => (
          <div key={c.id} className="flex items-center gap-3 p-2.5 rounded-lg border border-slate-200">
            <div className="h-8 w-8 rounded-md bg-slate-100 flex items-center justify-center"><Hash className="h-4 w-4 text-slate-500" /></div>
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold text-slate-800 truncate">{c.name}</div>
              <div className="text-[11px] text-slate-400 truncate">{c.description || `${c.memberCount} member${c.memberCount === 1 ? "" : "s"}`}</div>
            </div>
            <button onClick={() => join(c.id)} data-testid={`join-channel-${c.id}`} className="h-7 px-3 rounded-md bg-blue-600 text-white text-[12px] font-semibold hover:bg-blue-700">Join</button>
          </div>
        ))}
      </div>
    </Shell>
  );
}

export function AddMembersModal({ members, existingIds, onClose, onAdded }) {
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!selected.length) return onClose();
    setSaving(true);
    try {
      await onAdded(selected);
    } finally {
      setSaving(false);
    }
  };
  return (
    <Shell title="Add people" onClose={onClose} testid="add-members-modal">
      <div className="p-5">
        <MemberMultiSelect
          members={members}
          selected={selected}
          excludeIds={existingIds}
          onToggle={(id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]))}
        />
      </div>
      <div className="px-5 py-3 border-t border-slate-100 flex justify-end gap-2">
        <button onClick={onClose} className="h-9 px-3.5 rounded-md text-[13px] font-semibold text-slate-600 hover:bg-slate-100">Cancel</button>
        <button onClick={save} disabled={saving || !selected.length} data-testid="add-members-submit" className="h-9 px-4 rounded-md bg-blue-600 text-white text-[13px] font-semibold hover:bg-blue-700 disabled:opacity-60">
          {saving ? "Adding…" : `Add ${selected.length || ""}`.trim()}
        </button>
      </div>
    </Shell>
  );
}

const SHARE_KINDS = [
  { kind: "job", label: "Job", icon: Briefcase, route: "/jobs", load: () => jobsApi.list(), labelOf: (j) => j.title || j.serviceType || j.jobNumber || `Job ${j.id?.slice(0, 6)}`, sub: (j) => j.customerName || j.customer?.name || j.status },
  { kind: "estimate", label: "Estimate", icon: FileText, route: "/estimates", load: () => estimatesApi.list(), labelOf: (e) => e.number || e.title || `Estimate ${e.id?.slice(0, 6)}`, sub: (e) => e.customerName || e.customer?.name || (e.total != null ? `$${e.total}` : e.status) },
  { kind: "customer", label: "Customer", icon: UserIcon, route: "/customers", load: () => customersApi.list(), labelOf: (c) => c.name, sub: (c) => c.email || c.phone || c.city },
];

export function ShareModal({ onClose, onShare }) {
  const [kind, setKind] = useState("job");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const cfg = useMemo(() => SHARE_KINDS.find((k) => k.kind === kind), [kind]);

  useEffect(() => {
    setLoading(true);
    cfg.load().then(setItems).catch(() => setItems([])).finally(() => setLoading(false));
  }, [cfg]);

  const filtered = items.filter((it) => cfg.labelOf(it)?.toLowerCase().includes(q.toLowerCase()));

  const pick = (it) => {
    onShare({ kind, id: it.id, label: cfg.labelOf(it), sub: cfg.sub(it) || "", url: `${cfg.route}?id=${it.id}` });
  };

  return (
    <Shell title="Share into chat" onClose={onClose} testid="share-modal">
      <div className="px-5 pt-4">
        <div className="flex gap-1.5 mb-3">
          {SHARE_KINDS.map((k) => {
            const Icon = k.icon;
            const on = k.kind === kind;
            return (
              <button
                key={k.kind}
                onClick={() => setKind(k.kind)}
                data-testid={`share-kind-${k.kind}`}
                className={`flex items-center gap-1.5 h-8 px-3 rounded-md text-[12.5px] font-semibold transition-colors ${on ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
              >
                <Icon className="h-3.5 w-3.5" /> {k.label}
              </button>
            );
          })}
        </div>
        <div className="relative mb-2">
          <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${cfg.label.toLowerCase()}s…`} className={`${inputCls} pl-8`} data-testid="share-search" />
        </div>
      </div>
      <div className="px-5 pb-5 max-h-72 overflow-y-auto space-y-1">
        {loading && <div className="text-[12px] text-slate-400 py-4 text-center">Loading…</div>}
        {!loading && filtered.length === 0 && <div className="text-[12px] text-slate-400 py-4 text-center">Nothing to share here yet.</div>}
        {filtered.slice(0, 50).map((it) => {
          const Icon = cfg.icon;
          return (
            <button
              key={it.id}
              onClick={() => pick(it)}
              data-testid={`share-item-${it.id}`}
              className="w-full flex items-center gap-3 p-2.5 rounded-lg border border-slate-200 hover:border-blue-300 hover:bg-blue-50/30 text-left transition-colors"
            >
              <div className="h-8 w-8 rounded-md bg-slate-100 flex items-center justify-center shrink-0"><Icon className="h-4 w-4 text-slate-500" /></div>
              <div className="min-w-0">
                <div className="text-[13px] font-semibold text-slate-800 truncate">{cfg.labelOf(it)}</div>
                <div className="text-[11px] text-slate-400 truncate">{cfg.sub(it)}</div>
              </div>
            </button>
          );
        })}
      </div>
    </Shell>
  );
}
